import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {useCallback, useMemo, useState} from 'react'
import { useDebounceValue } from 'usehooks-ts'

import http from '../../../lib/http'
import * as Table from '../../claim-history/simple-table'
import {useTableSort} from '../../claim-history/simple-table/use-sort-hook'
import { PageHeader } from '../components/page-header'
import { SearchInput } from '../components/search-input'
import styles from '../index.module.css'

// type from previous step
export interface CompassionUser extends Record<string, unknown> {
  id: string
  first: string
  last: string
  lower_first: string
  lower_last: string
  name: string
  dob: string
  ssn: string
  member: string
  member_portal_member_id: string
  client: string
  client_name: string
  group_number: string
  managing: string
  program: string
  status: string
  status_label: string
  phones: string[]
  team: string[]
  other: unknown[]
  mobile_applications_access: string[]
  member_portal_ids: string[]
  mpower_ids: string[]
  isVIPPatient: boolean
  is_vip_patient: boolean
  urgent: boolean
  last_date: string | null
  last_type: string | null
  inprogres: string | null
  onleave: string | null
  open: string | null
  my: string | null
  par: string | null
  created_at: string
}

function toIsoString(value?: string): string | undefined {
  if (!value) {
    return undefined
  }

  const trimmed = value.trim()
  if (trimmed.length === 0) {
    return undefined
  }

  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) {
    return undefined
  }

  return parsed.toISOString()
}

interface QueryData {
  rows: CompassionUser[]
  total: number
}

export function CompassionIndex(): JSX.Element {
  const [ query, setQuery ] = useState('')
  const [ page, setPage ] = useState(0) // 0-based like EnrollmentsPage
  const [ pageSize, setPageSize ] = useState(10)
  const {sort, setSort} = useTableSort<CompassionUser>({field: 'created_at', direction: 'desc'})
  const [createdRange, setCreatedRange] = useState<{ from?: string; to?: string }>({})
  const [memberFilter, setMemberFilter] = useState('')
  const [portalMemberFilter, setPortalMemberFilter] = useState('')
  const [groupNumberFilter, setGroupNumberFilter] = useState('')
  const [clientFilter, setClientFilter] = useState('')

  const [ debouncedQuery ] = useDebounceValue(query, 600)
  const createdFromIso = useMemo(() => toIsoString(createdRange.from), [createdRange.from])
  const createdToIso = useMemo(() => toIsoString(createdRange.to), [createdRange.to])

  const { data, status } = useQuery<QueryData>({
    queryKey: [
      'compassion-users',
      {
        query: debouncedQuery,
        page,
        pageSize,
        sortField: sort.field,
        sortDirection: sort.direction,
        createdFrom: createdFromIso ?? null,
        createdTo: createdToIso ?? null,
        member: memberFilter || null,
        portalMemberId: portalMemberFilter || null,
        groupNumber: groupNumberFilter || null,
        clientName: clientFilter || null
      }
    ],
    queryFn: async () => {
      const sp = new URLSearchParams()
      sp.set('pageSize', pageSize.toString())
      sp.set('page', page.toString())
      sp.set('sortBy', String(sort.field))
      sp.set('sortDir', sort.direction)

      const normalizedSearch = debouncedQuery.trim()
      if (normalizedSearch.length > 0) {
        sp.set('search', normalizedSearch)
      }

      if (createdFromIso) {
        sp.set('filters.createdFrom', createdFromIso)
      }

      if (createdToIso) {
        sp.set('filters.createdTo', createdToIso)
      }

      const normalizedMember = memberFilter.trim()
      if (normalizedMember.length > 0) {
        sp.set('filters.member', normalizedMember)
      }

      const normalizedPortalMember = portalMemberFilter.trim()
      if (normalizedPortalMember.length > 0) {
        sp.set('filters.portalMemberId', normalizedPortalMember)
      }

      const normalizedGroupNumber = groupNumberFilter.trim()
      if (normalizedGroupNumber.length > 0) {
        sp.set('filters.groupNumber', normalizedGroupNumber)
      }

      const normalizedClientName = clientFilter.trim()
      if (normalizedClientName.length > 0) {
        sp.set('filters.clientName', normalizedClientName)
      }

      const response = await http.getRequest(`/admin/compassion-patients?${sp.toString()}`)
      return response
    },
    placeholderData: keepPreviousData
  })

  const columns = useMemo<Array<Table.Column<CompassionUser>>>(() => [
    {
      name: 'id',
      header: 'ID',
      isSortable: false,
      getValue: (entry) => (
          <a href={`/patient/${entry.client}/${entry.id}`}>{entry.first} {entry.last}</a>
      )
    },
    {
      name: 'phones',
      header: 'Phones',
      isSortable: false,
      getValue: entry => entry.phones?.join(', ') || '—'
    },
    {
      name: 'member',
      header: 'Member',
      isSortable: true
    },
    {
      name: 'member_portal_member_id',
      header: 'Portal Member ID',
      isSortable: true
    },
    {
      name: 'group_number',
      header: 'Group Number',
      isSortable: true
    },
    {
      name: 'client_name',
      header: 'Client Name',
      isSortable: true
    },
    {
      name: 'enrolled',
      header: 'Enrolled',
      getValue: (entry) => {
        const title = `
        Member Portal Ids:\n\t
           ${entry.member_portal_ids.join('\n\t')}
           
        Mpower Ids:\n\t
              ${entry.mpower_ids.join('\n\t')}
        `
        return <span title={title}>{entry.mobile_applications_access?.join(', ') || '—'}</span>
      }
    },
    {
      name: 'created_at',
      header: 'Created At',
      isSortable: true,
      initialSortDirection: 'desc',
      getValue: (entry) => (
          <span style={{whiteSpace: 'nowrap'}}>
          {entry.created_at ? Table.formatTime(entry.created_at) : '—'}
        </span>
      )
    },
  ], [])

  const filters = useMemo<Table.Filter[]>(() => {
    const hasFrom = Boolean(createdRange.from)
    const hasTo = Boolean(createdRange.to)

    return [
      {
        name: 'member',
        value: memberFilter ? [memberFilter] : [],
        render: ({onChange, value}) => {
          const current = typeof value?.[0] === 'string' ? value[0] as string : memberFilter

          return (
              <div style={{padding: '0.75rem', minWidth: 260}}>
                <label
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.25rem',
                      fontSize: '0.875rem'
                    }}
                >
                  <span>Member</span>
                  <input
                      type='text'
                      value={current}
                      placeholder='Enter member ID'
                      onChange={event => {
                        const nextValue = event.target.value
                        onChange(nextValue ? [nextValue] : [])
                      }}
                      style={{padding: '0.5rem', borderRadius: 8, border: '1px solid #d4d4d8'}}
                  />
                </label>
              </div>
          )
        },
        isActive: Boolean(memberFilter.trim())
      },
      {
        name: 'member_portal_member_id',
        value: portalMemberFilter ? [portalMemberFilter] : [],
        render: ({onChange, value}) => {
          const current = typeof value?.[0] === 'string' ? value[0] as string : portalMemberFilter

          return (
              <div style={{padding: '0.75rem', minWidth: 260}}>
                <label
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.25rem',
                      fontSize: '0.875rem'
                    }}
                >
                  <span>Portal Member ID</span>
                  <input
                      type='text'
                      value={current}
                      placeholder='Enter portal member ID'
                      onChange={event => {
                        const nextValue = event.target.value
                        onChange(nextValue ? [nextValue] : [])
                      }}
                      style={{padding: '0.5rem', borderRadius: 8, border: '1px solid #d4d4d8'}}
                  />
                </label>
              </div>
          )
        },
        isActive: Boolean(portalMemberFilter.trim())
      },
      {
        name: 'group_number',
        value: groupNumberFilter ? [groupNumberFilter] : [],
        render: ({onChange, value}) => {
          const current = typeof value?.[0] === 'string' ? value[0] as string : groupNumberFilter

          return (
              <div style={{padding: '0.75rem', minWidth: 260}}>
                <label
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.25rem',
                      fontSize: '0.875rem'
                    }}
                >
                  <span>Group Number</span>
                  <input
                      type='text'
                      value={current}
                      placeholder='Enter group number'
                      onChange={event => {
                        const nextValue = event.target.value
                        onChange(nextValue ? [nextValue] : [])
                      }}
                      style={{padding: '0.5rem', borderRadius: 8, border: '1px solid #d4d4d8'}}
                  />
                </label>
              </div>
          )
        },
        isActive: Boolean(groupNumberFilter.trim())
      },
      {
        name: 'client_name',
        value: clientFilter ? [clientFilter] : [],
        render: ({onChange, value}) => {
          const current = typeof value?.[0] === 'string' ? value[0] as string : clientFilter

          return (
              <div style={{padding: '0.75rem', minWidth: 260}}>
                <label
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.25rem',
                      fontSize: '0.875rem'
                    }}
                >
                  <span>Client</span>
                  <input
                      type='text'
                      value={current}
                      placeholder='Enter client name'
                      onChange={event => {
                        const nextValue = event.target.value
                        onChange(nextValue ? [nextValue] : [])
                      }}
                      style={{padding: '0.5rem', borderRadius: 8, border: '1px solid #d4d4d8'}}
                  />
                </label>
              </div>
          )
        },
        isActive: Boolean(clientFilter.trim())
      },
      {
        name: 'created_at',
        value: [
          createdRange.from ?? '',
          createdRange.to ?? ''
        ],
        render: ({onChange, value}) => {
          const currentValues = Array.isArray(value) ? value : []
          const fromValue = typeof currentValues[0] === 'string' ? currentValues[0] as string : ''
          const toValue = typeof currentValues[1] === 'string' ? currentValues[1] as string : ''

          const updateFrom = (nextValue: string) => {
            onChange([nextValue, toValue])
          }

          const updateTo = (nextValue: string) => {
            onChange([fromValue, nextValue])
          }

          return (
              <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    minWidth: 260
                  }}
              >
                <label
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.25rem',
                      fontSize: '0.875rem'
                    }}
                >
                  <span>Created From</span>
                  <input
                      type='datetime-local'
                      value={fromValue}
                      max={toValue || undefined}
                      onChange={event => updateFrom(event.target.value)}
                      style={{padding: '0.5rem', borderRadius: 8, border: '1px solid #d4d4d8'}}
                  />
                </label>

                <label
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.25rem',
                      fontSize: '0.875rem'
                    }}
                >
                  <span>Created To</span>
                  <input
                      type='datetime-local'
                      value={toValue}
                      min={fromValue || undefined}
                      onChange={event => updateTo(event.target.value)}
                      style={{padding: '0.5rem', borderRadius: 8, border: '1px solid #d4d4d8'}}
                  />
                </label>
              </div>
          )
        },
        isActive: hasFrom || hasTo
      }
    ]
  }, [
    createdRange.from,
    createdRange.to,
    memberFilter,
    portalMemberFilter,
    groupNumberFilter,
    clientFilter
  ])

  const pagination = useMemo(() => ({
    page,
    pageSize,
    totalFiltered: data?.total ?? 0,
    totalUnfiltered: data?.total ?? 0
  }), [ page, pageSize, data?.total ])

  const handleFilterChange = useCallback((field: keyof CompassionUser, values: Array<string | number | boolean>) => {
    const getSingleValue = (): string => {
      const candidate = (values ?? [])[0]
      return typeof candidate === 'string' ? candidate : ''
    }

    if (field === 'created_at') {
      const [fromRaw, toRaw] = (values ?? []) as string[]

      setCreatedRange({
        from: typeof fromRaw === 'string' && fromRaw.trim().length > 0 ? fromRaw : undefined,
        to: typeof toRaw === 'string' && toRaw.trim().length > 0 ? toRaw : undefined
      })
      setPage(0)
      return
    }

    const nextValue = getSingleValue()

    switch (field) {
      case 'member':
        setMemberFilter(nextValue)
        setPage(0)
        break
      case 'member_portal_member_id':
        setPortalMemberFilter(nextValue)
        setPage(0)
        break
      case 'group_number':
        setGroupNumberFilter(nextValue)
        setPage(0)
        break
      case 'client_name':
        setClientFilter(nextValue)
        setPage(0)
        break
      default:
        break
    }
  }, [setCreatedRange, setMemberFilter, setPortalMemberFilter, setGroupNumberFilter, setClientFilter, setPage])

  const handleFiltersClear = useCallback(() => {
    setCreatedRange({})
    setQuery('')
    setMemberFilter('')
    setPortalMemberFilter('')
    setGroupNumberFilter('')
    setClientFilter('')
    setPage(0)
  }, [setCreatedRange, setQuery, setMemberFilter, setPortalMemberFilter, setGroupNumberFilter, setClientFilter, setPage])

  const handleSortChange = useCallback((field: keyof CompassionUser, direction: 'asc' | 'desc') => {
    setSort({field, direction})
    setPage(0)
  }, [setSort, setPage])

  const hasQuery = query.trim().length > 0
  const hasCreatedFilter = Boolean(createdRange.from || createdRange.to)
  const hasAdditionalFilters = [memberFilter, portalMemberFilter, groupNumberFilter, clientFilter].some(value => value.trim().length > 0)
  const isDefaultState = !hasQuery && !hasCreatedFilter && !hasAdditionalFilters

  return (
    <div>
      <PageHeader className={styles.pageHeader}>
        <h1>Compassion Index</h1>
        <SearchInput
          value={query}
          placeholder='Search by patient info'
          onChange={event => {
            setQuery(event.target.value)
            setPage(0) // reset to first page on new search
          }}
        />
      </PageHeader>

      <Table.Table
        data={data?.rows ?? []}
        queryStatus={status}
        noDataPlaceholder='Compassion index empty'
        forceClearFilters={!isDefaultState}
        columns={columns}
        filters={filters}
        sort={sort}
        pagination={pagination}
        onFilterChange={handleFilterChange}
        onFiltersClear={handleFiltersClear}
        onSortChange={handleSortChange}
        onPageChange={(value: number) => setPage(value - 1)} // Table gives 1-based
        onPageSizeChange={(value: number) => {
          setPageSize(value)
          setPage(0)
        }}
      />
    </div>
  )
}
